import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiJson } from './api';
import { useSEO } from './useSEO';

export function AdminNotesJobs() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState('');

  useSEO({ title: 'Admin: notes jobs', canonicalPath: '/admin/notes/jobs' });

  useEffect(() => {
    if (user === null) navigate('/login');
    else if (user && !user.isAdmin) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    const load = () => apiJson('/api/admin/notes/jobs', { token })
      .then((d) => alive && setJobs(d.jobs || []))
      .catch((e) => alive && setError(e.message));
    load();
    const i = setInterval(load, 5000);
    return () => { alive = false; clearInterval(i); };
  }, [token]);

  return (
    <div className="tt-page" style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h1>Notes jobs</h1>
        <Link to="/admin/notes" className="primary-btn">+ New job</Link>
      </div>
      {error && <p style={{ color: '#c33' }}>{error}</p>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginTop: 12 }}>
        <thead>
          <tr style={{ textAlign: 'left' }}>
            <th>Created</th><th>Subject</th><th>Mode</th><th>Status</th><th>Progress</th><th></th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id} style={{ borderTop: '1px solid #eee' }}>
              <td>{new Date(j.createdAt).toLocaleString()}</td>
              <td>{j.level} · {j.subject} · {j.board}</td>
              <td>{j.mode}</td>
              <td>
                <span style={{ color: j.status === 'failed' ? '#c33' : j.status === 'completed' ? '#3a7' : '#666' }}>
                  {j.status}
                </span>
              </td>
              <td>{j.progressPct}% · {j.progressMessage || ''}</td>
              <td><Link to={`/admin/notes/jobs/${j.id}`}>open</Link></td>
            </tr>
          ))}
          {!jobs.length && <tr><td colSpan="6" style={{ color: 'var(--muted)', padding: 20 }}>No jobs yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function AdminNotesJobDetail() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useSEO({ title: 'Admin: notes job', canonicalPath: `/admin/notes/jobs/${id}` });

  useEffect(() => {
    if (user === null) navigate('/login');
    else if (user && !user.isAdmin) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (!token || !id) return;
    let alive = true;
    const load = () => apiJson(`/api/admin/notes/jobs/${id}`, { token })
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.message));
    load();
    const i = setInterval(load, 4000);
    return () => { alive = false; clearInterval(i); };
  }, [token, id]);

  if (!data) return <div className="tt-page">{error || 'Loading…'}</div>;
  const { job, sources, outputs } = data;

  return (
    <div className="tt-page" style={{ maxWidth: 1100 }}>
      <Link to="/admin/notes/jobs">← All jobs</Link>
      <h1>Job · {job.subject} ({job.board})</h1>
      <p style={{ color: 'var(--muted)' }}>
        {job.level} · mode <strong>{job.mode}</strong> · status <strong>{job.status}</strong> · {job.progressPct}%
      </p>
      {job.error && <p style={{ color: '#c33' }}>{job.error}</p>}

      <h3>Sources</h3>
      <ul>{sources.map((s) => <li key={s.id}>{s.original_filename} ({s.text_length || 0} chars extracted)</li>)}</ul>

      <h3>Outputs</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead><tr style={{ textAlign: 'left' }}><th>Code</th><th>Topic</th><th>Status</th><th>Missing</th><th></th></tr></thead>
        <tbody>
          {outputs.map((o) => (
            <tr key={o.id} style={{ borderTop: '1px solid #eee' }}>
              <td>{o.code || '—'}</td>
              <td>{o.title}</td>
              <td>{o.status}</td>
              <td>{(o.missingStatements || []).length}</td>
              <td><Link to={`/admin/notes/jobs/${id}/outputs/${o.id}`}>view</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminNotesOutputView() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { id, outputId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useSEO({ title: 'Admin: notes output', canonicalPath: `/admin/notes/jobs/${id}/outputs/${outputId}` });

  useEffect(() => {
    if (user === null) navigate('/login');
    else if (user && !user.isAdmin) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (!token) return;
    apiJson(`/api/admin/notes/jobs/${id}/outputs/${outputId}`, { token })
      .then(setData).catch((e) => setError(e.message));
  }, [token, id, outputId]);

  if (!data) return <div className="tt-page">{error || 'Loading…'}</div>;

  return (
    <div className="tt-page" style={{ maxWidth: 1000 }}>
      <Link to={`/admin/notes/jobs/${id}`}>← Job</Link>
      <h1>{data.code ? `${data.code} · ` : ''}{data.title}</h1>
      <p style={{ color: 'var(--muted)' }}>Status: <strong>{data.status}</strong></p>

      {(data.missingStatements || []).length > 0 && (
        <div style={{ background: '#fff4e5', border: '1px solid #f5b061', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          <strong>⚠ Spec coverage gaps ({data.missingStatements.length})</strong>
          <ul>{data.missingStatements.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      <h3>Student notes</h3>
      <pre style={preStyle}>{JSON.stringify(data.studentNotes, null, 2)}</pre>

      <h3>AI tutor notes</h3>
      <pre style={preStyle}>{JSON.stringify(data.aiNotes, null, 2)}</pre>

      <h3>Coverage audit</h3>
      <pre style={preStyle}>{JSON.stringify(data.coverage, null, 2)}</pre>
    </div>
  );
}

const preStyle = {
  background: '#fafafa',
  border: '1px solid #ddd',
  borderRadius: 6,
  padding: 12,
  fontSize: 12,
  maxHeight: 480,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
};
